import { useFormContext, Controller } from 'react-hook-form';
import { useTranslation } from '@/utils/useTranslation';
import {
  isValidEmail,
  isValidPassword,
  isConfirmationEmailValid,
  hasMinimum8Characters,
  hasMaximum20Characters,
  hasLowerCaseLetter,
  hasUpperCaseLetter,
  hasNumber,
  hasSymbol,
} from '@/utils/validations';
import { SectionHeading } from '@/components/SectionHeading';
import { Box, PasswordField, TextField } from '@alto-avios/alto-ui';

export const LoginDetails = () => {
  const {
    watch,
    control,
    formState: { errors },
  } = useFormContext();
  const { t } = useTranslation('loginDetails');

  const passwordValue = watch('password') || '';

  return (
    <Box display="flex" flexDirection="column" gap="sm">
      <SectionHeading title={t('heading')} />

      <Controller
        name="email"
        control={control}
        defaultValue=""
        rules={{
          required: t('fieldRequired'),
          validate: (email: string) => {
            const result = isValidEmail(email);

            if (!result.isValid) {
              const { error } = result;
              switch (error.type) {
                case 'INVALID_EMAIL':
                  return t('email.error');
              }
            }

            return true;
          },
        }}
        render={({ field }) => (
          <TextField
            id="email"
            type="email"
            data-testid="email"
            label={t('email.label')}
            isRequired
            value={field.value}
            onChange={field.onChange}
            isInvalid={!!errors['email']}
            errorMessage={errors['email']?.message as string}
          />
        )}
      />

      <Controller
        name="confirmEmail"
        control={control}
        defaultValue=""
        rules={{
          required: t('fieldRequired'),
          validate: (confirmationEmail: string) => {
            if (!isConfirmationEmailValid(watch('email'), confirmationEmail))
              return t('confirmEmail.error');
          },
        }}
        render={({ field }) => (
          <TextField
            id="confirmEmail"
            type="email"
            data-testid="confirmEmail"
            label={t('confirmEmail.label')}
            isRequired
            value={field.value}
            onChange={field.onChange}
            isInvalid={!!errors['confirmEmail']}
            errorMessage={errors['confirmEmail']?.message as string}
          />
        )}
      />

      <Controller
        name="password"
        control={control}
        defaultValue=""
        rules={{
          required: t('fieldRequired'),
          validate: (password: string) => {
            return isValidPassword(password) || t('password.error');
          },
        }}
        render={({ field, fieldState }) => (
          <PasswordField
            data-testid="password-input"
            isRequired
            label={t('password.label')}
            value={field.value || ''}
            onChange={field.onChange}
            isInvalid={!!fieldState.error}
            errorMessage={fieldState.error?.message}
            screenReaderStrengthLabels={{
              invalid: t('password.screenReaderStrengthLabels.invalid'),
              neutral: t('password.screenReaderStrengthLabels.neutral'),
              valid: t('password.screenReaderStrengthLabels.valid'),
            }}
            strengthValidations={[
              {
                message: t('password.conditions.min8char'),
                validation: () => hasMinimum8Characters(passwordValue),
              },
              {
                message: t('password.conditions.max20char'),
                validation: () => hasMaximum20Characters(passwordValue),
              },
              {
                message: t('password.conditions.isLowerCasePresent'),
                validation: () => hasLowerCaseLetter(passwordValue),
              },
              {
                message: t('password.conditions.isUpperCasePresent'),
                validation: () => hasUpperCaseLetter(passwordValue),
              },
              {
                message: t('password.conditions.isNumberPresent'),
                validation: () => hasNumber(passwordValue),
              },
              {
                message: t('password.conditions.isSymbolPresent'),
                validation: () => hasSymbol(passwordValue),
              },
            ]}
          />
        )}
      />
    </Box>
  );
};
